import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import type { WorkspacePlanSummary } from "@/types/workspace";

// ──────────────────────────────────────────────────────────────────────
// Feature Keys — add new features here as the platform grows
// ──────────────────────────────────────────────────────────────────────
export type PlatformFeature =
  | "universal_key"      // BYOK / universal API key
  | "published_apps"     // Publishing / deploying apps
  | "github_connect"     // Connecting & syncing GitHub repos
  | "private_projects"   // Creating private (not public) projects
  | "create_team"        // Creating a team workspace
  | "invite_members"     // Inviting team members
  | "custom_domains"     // Custom domain mapping
  | "ui_editor"          // Visual UI editor (allowed on free w/ limitations, but listed)
  | "fork_project"       // Forking (team/GitHub branch forks require paid)
  | "role_permissions"   // Full RBAC inside teams
  | "engage_expert"      // Human-in-the-loop expert engagement
  | "risk_review"        // AI-powered code confidence scoring and risk analysis
  | "autonomous_mode";   // Power/autonomous agent mode (code review + visual testing)

// ──────────────────────────────────────────────────────────────────────
// Feature → minimum plan tier required for full access
//
// Tier hierarchy (sort_order in the DB / plan name patterns):
//   free  →  starter  →  pro  →  team
//
// Convention: list every feature's required plan.
// If the current plan's sort_order >= the required sort_order → access granted.
// ──────────────────────────────────────────────────────────────────────
const FEATURE_PLAN_REQUIREMENTS: Record<PlatformFeature, "paid" | "free"> = {
  universal_key: "free",
  published_apps: "paid",
  github_connect: "paid",
  private_projects: "paid",
  create_team: "free",      // Starting a team is how users buy a team plan, so it is never locked
  invite_members: "paid",
  custom_domains: "paid",
  ui_editor: "paid",       // Basic access is free; full features require paid
  fork_project: "paid",
  role_permissions: "paid",
  engage_expert: "paid",
  risk_review: "paid",
  autonomous_mode: "paid",
};

// Human-readable display names and descriptions for the upgrade tooltip
export const FEATURE_META: Record<
  PlatformFeature,
  { title: string; description: string }
> = {
  universal_key: {
    title: "Universal AI Key",
    description:
      "Bring your own API key to access any AI provider (OpenAI, Anthropic, Google) directly through Joylo.",
  },
  published_apps: {
    title: "Published Apps",
    description:
      "Publish and host your apps with a public URL, custom domain support, and managed deployment.",
  },
  github_connect: {
    title: "GitHub Integration",
    description:
      "Connect your GitHub account to sync code, push commits, open PRs, and collaborate with version control.",
  },
  private_projects: {
    title: "Private Projects",
    description:
      "Keep your projects private so only you and your invited team members can see and edit them.",
  },
  create_team: {
    title: "More Workspaces",
    description:
      "Create another workspace with its own plan, then invite colleagues to share its projects and credits.",
  },
  invite_members: {
    title: "Invite Members",
    description:
      "Invite colleagues into your workspace as editors or admins.",
  },
  custom_domains: {
    title: "Custom Domains",
    description:
      "Map your own domain name to your published Joylo apps for a professional look.",
  },
  ui_editor: {
    title: "UI Editor",
    description:
      "Use the visual UI editor to design and modify your app's interface with drag-and-drop precision.",
  },
  fork_project: {
    title: "Project Forking",
    description:
      "Fork any project into one of your workspaces or create a GitHub branch fork with full PR workflow.",
  },
  role_permissions: {
    title: "Role-Based Permissions",
    description:
      "Give workspace members the Owner, Admin or Editor role to control who manages billing and members.",
  },
  engage_expert: {
    title: "Engage Expert",
    description:
      "Get direct help from expert developers to fix complex bugs, build features, or optimize your code.",
  },
  risk_review: {
    title: "Confidence Score",
    description:
      "AI-powered confidence scoring for your generated code",
  },
  autonomous_mode: {
    title: "Power Mode",
    description:
      "Unlock full autonomous capabilities. Agent reviews its own code, runs visual tests, and fixes issues automatically.",
  },
};

// ──────────────────────────────────────────────────────────────────────
// Helper: determine whether the current user is on any paid plan
// ──────────────────────────────────────────────────────────────────────
function isOnPaidPlan(planName: string | undefined): boolean {
  if (!planName) return false;
  const lower = planName.toLowerCase();
  // "No Plan" (no subscription), "Free", or any name containing "free" → not paid
  if (lower === "no plan" || lower === "none") return false;
  return !lower.includes("free");
}

// ──────────────────────────────────────────────────────────────────────
// Main hook
// ──────────────────────────────────────────────────────────────────────
function useActiveWorkspacePlan(): WorkspacePlanSummary | null | undefined {
  try {
    return useWorkspace().activeWorkspace?.subscription;
  } catch {
    // Rendered outside WorkspaceProvider (isolated tests, pre-auth screens): only the personal plan is known
    return undefined;
  }
}

export function usePlanFeatures() {
  const { user } = useAuth();
  const workspacePlan = useActiveWorkspacePlan();

  // The active workspace's own plan pays for work there (JA-1018); without one, the personal plan from user.subscription applies
  const personal = (user as { subscription?: { plan_name?: string; status?: string; current_period_end?: string | null } } | null)?.subscription;
  const subscription = workspacePlan ?? personal;
  const planName: string | undefined = subscription?.plan_name ?? undefined;
  const subscriptionStatus: string | undefined = subscription?.status ?? undefined;
  const currentPeriodEnd: string | null = subscription?.current_period_end ?? null;

  const isPaid = useMemo(() => {
    // No active subscription means free
    if (subscriptionStatus === "none" || subscriptionStatus === "canceled" || subscriptionStatus === "expired") return false;
    return isOnPaidPlan(planName);
  }, [planName, subscriptionStatus]);

  // Distinguishes "never had a paid plan" from "had one, it lapsed" so upgrade
  // prompts can say "Renew subscription" instead of "Upgrade" for the latter.
  const isExpired = useMemo(() => {
    if (isPaid) return false;
    return isOnPaidPlan(planName) && (subscriptionStatus === "expired" || subscriptionStatus === "canceled");
  }, [isPaid, planName, subscriptionStatus]);

  /**
   * Returns true when the user CAN access the feature.
   */
  const hasAccess = (feature: PlatformFeature): boolean => {
    const requirement = FEATURE_PLAN_REQUIREMENTS[feature];
    if (requirement === "paid") return isPaid;
    return true;
  };

  /**
   * Returns true when the user is BLOCKED from the feature (for rendering locks).
   */
  const isRestricted = (feature: PlatformFeature): boolean => !hasAccess(feature);

  return {
    isPaid,
    isExpired,
    planName,
    planStatus: subscriptionStatus,
    currentPeriodEnd,
    hasAccess,
    isRestricted,
  };
}

// "Nov 19, 2026" for a plan period end, or null when there's no usable date
export function formatPlanDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Shared copy for upgrade CTAs across the app: a lapsed paid plan asks the user
// to renew rather than "upgrade" to the plan they already had.
export function getUpgradeCtaLabel(isExpired: boolean): string {
  return isExpired ? "Renew subscription" : "Upgrade to Solo Builder";
}
